import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import CrawlPage from './CrawlPage';
import ProgressPage from './ProgressPage';
import URLValidationPage from './URLValidationPage';
import { getUser, isPlatformAdmin } from '../lib/auth';
import {
  Search, Activity, Link as LinkIcon, ChevronDown, ChevronUp, Wifi, WifiOff,
  CheckCircle, AlertTriangle, History,
} from 'lucide-react';

const API_BASE = '';
const UPLOAD_HISTORY_PAGE_SIZE = 10;

function MerchantScanPage() {
  const navigate = useNavigate();
  const isPoc = !!getUser()?.is_poc;
  const showTenantCol = isPlatformAdmin();
  const [activeTab, setActiveTab] = useState('crawl');
  const [isCrawlOpen, setIsCrawlOpen] = useState(true);
  const [isProgressOpen, setIsProgressOpen] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [uploadPage, setUploadPage] = useState(0);

  // WebSocket status is owned by CrawlPage (connectWebSocket runs there); parent only displays it.
  const [wsConnected, setWsConnected] = useState(false);
  const [crawlID, setCrawlID] = useState(null);

  const handleConnectionChange = useCallback(({ wsConnected: connected, crawlID: id }) => {
    setWsConnected(connected);
    setCrawlID(id);
  }, []);

  useEffect(() => {
    if (isPoc && activeTab !== 'crawl') setActiveTab('crawl');
  }, [isPoc, activeTab]);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [merchantsRes, failedRes] = await Promise.all([
          fetch(`${API_BASE}/api/merchants`),
          fetch(`${API_BASE}/api/merchants/manual-retry-queue`),
        ]);
        if (merchantsRes.ok) {
          const data = await merchantsRes.json();
          setSuccessCount(Array.isArray(data) ? data.length : (data.merchants?.length || 0));
        }
        if (failedRes.ok) {
          const data = await failedRes.json();
          const items = data.failed_merchants || data.items || data || [];
          setFailedCount(Array.isArray(items) ? items.length : 0);
        }
      } catch (err) {
        console.warn('Could not load merchant counts', err);
      }
    };
    fetchCounts();
  }, []);

  useEffect(() => {
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        // Platform admin may optionally scope via viewing_tenant_id.
        // Tenant / POC users: backend scopes from JWT (ignore client tenant_id).
        const tenantId = showTenantCol ? localStorage.getItem('viewing_tenant_id') : null;
        const qs = tenantId && !['all', 'null', 'undefined'].includes(String(tenantId).toLowerCase())
          ? `?tenant_id=${encodeURIComponent(tenantId)}`
          : '';
        const [nativeRes, uwRes] = await Promise.all([
          fetch(`${API_BASE}/api/merchants/upload-history${qs}`),
          fetch(`${API_BASE}/api/underwriting/scan-events/${qs}`),
        ]);
        const native = nativeRes.ok ? await nativeRes.json() : { uploads: [] };
        const uw = uwRes.ok ? await uwRes.json() : { uploads: [] };
        const uwUploads = Array.isArray(uw.uploads) ? uw.uploads : [];
        const nativeUploads = Array.isArray(native.uploads) ? native.uploads : [];
        // Prefer Underwriting-labeled rows; avoid duplicate URL entries from native history.
        const uwKeys = new Set(
          uwUploads.map((u) => String(u.id || '').toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, ''))
        );
        const filteredNative = nativeUploads.filter((u) => {
          const key = String(u.id || '').toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
          return !uwKeys.has(key);
        });
        const merged = [...uwUploads, ...filteredNative].sort((a, b) => {
          const ta = a.started_at ? new Date(a.started_at).getTime() : 0;
          const tb = b.started_at ? new Date(b.started_at).getTime() : 0;
          return tb - ta;
        });
        setUploadHistory(merged);
        setUploadPage(0);
      } catch (err) {
        console.warn('Could not load upload history', err);
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();
  }, [showTenantCol]);

  const uploadPageData = useMemo(() => {
    const total = uploadHistory.length;
    const totalPages = Math.max(1, Math.ceil(total / UPLOAD_HISTORY_PAGE_SIZE));
    const safePage = Math.min(uploadPage, totalPages - 1);
    const slice = uploadHistory.slice(
      safePage * UPLOAD_HISTORY_PAGE_SIZE,
      (safePage + 1) * UPLOAD_HISTORY_PAGE_SIZE,
    );
    return { slice, total, totalPages, safePage };
  }, [uploadHistory, uploadPage]);

  return (
    <div className="w-full space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Merchant / URL Scan</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {isPoc
            ? 'Crawl merchant URLs and review classification history (POC)'
            : 'Crawl merchants, monitor progress, and validate URLs'}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { if (!isPoc || v === 'crawl') setActiveTab(v); }} className="w-full">
        {!isPoc && (
          <TabsList className="grid w-full grid-cols-2 h-8 sm:h-9 md:h-10">
            <TabsTrigger value="crawl" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <Search className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Crawl & Progress</span>
            </TabsTrigger>
            <TabsTrigger value="validation" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <LinkIcon className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>URL Validation</span>
            </TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="crawl" className="mt-6 space-y-6">
          <Collapsible open={isCrawlOpen} onOpenChange={setIsCrawlOpen}>
            <Card>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <div className="flex items-start justify-between gap-4 w-full">
                    <div className="min-w-0 flex-1 text-left">
                      <CardTitle className="flex items-center gap-2">
                        <Search className="w-5 h-5 shrink-0" />
                        Merchant Crawl & Process
                      </CardTitle>
                      <CardDescription className="mt-1.5 text-left">
                        Submit URLs to crawl and process merchant data
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1.5"
                        onClick={(e) => { e.stopPropagation(); navigate('/reports?view=successful'); }}
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                        Success ({successCount})
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1.5"
                        onClick={(e) => { e.stopPropagation(); navigate('/reports?view=failed'); }}
                      >
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Failed ({failedCount})
                      </Button>
                      {wsConnected ? (
                        <Badge variant="default" className="flex items-center gap-1">
                          <Wifi className="w-3 h-3" />
                          <span>Connected</span>
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <WifiOff className="w-3 h-3" />
                          <span>Disconnected</span>
                        </Badge>
                      )}
                      {crawlID && (
                        <Badge variant="outline" className="text-xs font-mono">
                          ID: {crawlID}
                        </Badge>
                      )}
                      {isCrawlOpen ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <CrawlPage onConnectionChange={handleConnectionChange} />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {!isPoc && (
            <Collapsible open={isProgressOpen} onOpenChange={setIsProgressOpen}>
              <Card>
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Activity className="w-5 h-5" />
                          Progress Monitor
                        </CardTitle>
                        <CardDescription>Track real-time processing status and pipeline activity</CardDescription>
                      </div>
                      {isProgressOpen ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent>
                    <ProgressPage />
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <History className="w-4 h-4" />
                Upload History
              </CardTitle>
              <CardDescription>
                Single and bulk uploads with status and tenant. Hub-triggered runs show Source = PayLegit Underwriting.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <p className="text-sm text-muted-foreground">Loading upload history…</p>
              ) : uploadHistory.length === 0 ? (
                <p className="text-sm text-muted-foreground">No uploads recorded yet.</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Source</TableHead>
                          <TableHead>Type</TableHead>
                          {showTenantCol && <TableHead>Tenant</TableHead>}
                          <TableHead>Status</TableHead>
                          <TableHead>URLs</TableHead>
                          <TableHead>Success</TableHead>
                          <TableHead>Failed</TableHead>
                          <TableHead>Started</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {uploadPageData.slice.map((row, idx) => (
                          <TableRow key={`${row.upload_type}-${row.id}-${uploadPageData.safePage}-${idx}`}>
                            <TableCell>
                              <span className="text-xs font-medium">
                                {row.source || (String(row.source_label || '') || 'Online MCC')}
                              </span>
                              {row.merchant_url ? (
                                <div className="text-[10px] text-muted-foreground mt-0.5 break-all">
                                  {String(row.merchant_url).replace(/^https?:\/\//, '')}
                                </div>
                              ) : row.hub_scan_ref ? (
                                <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                                  {row.hub_scan_ref}
                                </div>
                              ) : null}
                            </TableCell>
                            <TableCell className="capitalize">{row.upload_type}</TableCell>
                            {showTenantCol && (
                              <TableCell>{row.tenant_name || 'Platform'}</TableCell>
                            )}
                            <TableCell>{row.status || '—'}</TableCell>
                            <TableCell>{row.total_urls ?? '—'}</TableCell>
                            <TableCell>{row.successful ?? '—'}</TableCell>
                            <TableCell>{row.failed ?? '—'}</TableCell>
                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                              {row.started_at ? new Date(row.started_at).toLocaleString() : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {uploadPageData.totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-3 border-t">
                      <span className="text-xs text-muted-foreground">
                        Showing {uploadPageData.safePage * UPLOAD_HISTORY_PAGE_SIZE + 1}–
                        {Math.min((uploadPageData.safePage + 1) * UPLOAD_HISTORY_PAGE_SIZE, uploadPageData.total)} of {uploadPageData.total}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={uploadPageData.safePage === 0}
                          onClick={() => setUploadPage((p) => Math.max(0, p - 1))}
                          className="text-xs h-8 px-3"
                        >
                          ← Prev
                        </Button>
                        <span className="text-xs font-medium">
                          Page {uploadPageData.safePage + 1} of {uploadPageData.totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={uploadPageData.safePage >= uploadPageData.totalPages - 1}
                          onClick={() => setUploadPage((p) => Math.min(uploadPageData.totalPages - 1, p + 1))}
                          className="text-xs h-8 px-3"
                        >
                          Next →
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {!isPoc && (
          <TabsContent value="validation" className="mt-6">
            <URLValidationPage />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

export default MerchantScanPage;
